import { Flex, Icon, IconButton, Menu, MenuButton, MenuItem, MenuList, Text, Image } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { getConfigFromStorage } from '@/utils/config';
import { useTranslation } from 'react-i18next';
import { CustomTable } from '@/components/CustomTable/CustomTable';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { Anthem } from '@/model';
import { truncate } from '@/utils/string';

export interface AnthemTableProps {
  data: Anthem[];
  isLoading: boolean;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  sorting?: { field: string; desc: boolean };
  onSortingChange?: (field: string, desc: boolean) => void;
  onPaginationChange: (page: number, pageSize: number) => void;
  onEditClick: (item: Anthem) => void;
  onDeleteClick: (item: Anthem) => void;
}

export const AnthemTable = ({
  data,
  isLoading,
  pageNumber,
  pageSize,
  totalPages,
  sorting,
  totalItems,
  onSortingChange,
  onPaginationChange,
  onEditClick,
  onDeleteClick,
}: AnthemTableProps) => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<Anthem>();
  const isMultiTeam = getConfigFromStorage()?.is_multi_team ?? true;

  const columns = [
    columnHelper.accessor('created_at', {
      cell: props => props.getValue(),
      header: t('pages.application_history_anthem.anthem_table.anthem_table_created_at_header'),
    }),

    columnHelper.accessor('team_name', {
      cell: props => {
        const { team_image, team_name } = props.row.original;
        return (
          <Flex alignItems='center' gap={2}>
            {team_image ? <Image src={team_image} alt={team_name || ''} w='24px' h='24px' objectFit='contain' /> : null}
            <Text fontWeight='bold'>{team_name || ''}</Text>
          </Flex>
        );
      },
      header: t('pages.application_history_anthem.anthem_table.anthem_table_team_header'),
    }),

    columnHelper.accessor('location', {
      cell: props => {
        const url = props.getValue<string | undefined>();
        return url ? (
          <Flex align='center' gap={2}>
            <a href={url} target='_blank' rel='noopener noreferrer' download>
              <ChakraFeatherIcon name='music' cursor='pointer' color='brand.500' w='18px' h='18px' />
            </a>
          </Flex>
        ) : (
          <Text>-</Text>
        );
      },
      header: t('pages.application_history_anthem.anthem_table.anthem_table_location_header'),
    }),

    columnHelper.accessor('lyrics', {
      cell: props => {
        const text = props.getValue() || '';
        return <Text>{truncate(text, 50)}</Text>;
      },
      header: t('pages.application_history_anthem.anthem_table.anthem_table_lyrics_header'),
    }),

    columnHelper.display({
      id: 'actions',
      header: t('pages.application_history_anthem.anthem_table.anthem_table_view_details_option'),
      cell: props => (
        <Flex justifyContent={'end'}>
          <Menu>
            <MenuButton
              textColor='text.table'
              as={IconButton}
              variant='ghost'
              size='sm'
              icon={<Icon as={EllipsisVerticalIcon} />}
              aria-label={t('pages.application_history_anthem.anthem_table.anthem_table_show_more_actions')}
            />
            <MenuList textColor='white'>
              <MenuItem
                onClick={() => onEditClick?.(props.row.original as Anthem)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='eye' />
                  </Text>
                }
              >
                <Text>{t('pages.application_history_anthem.anthem_table.anthem_table_edit_button')}</Text>
              </MenuItem>
              <MenuItem
                onClick={() => onDeleteClick?.(props.row.original as Anthem)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='trash-2' color='red.500' w='18px' />
                  </Text>
                }
              >
                <Text>{t('pages.application_history_anthem.anthem_table.anthem_table_delete_button')}</Text>
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      ),
      enableSorting: false,
      meta: {
        align: 'right',
      },
    }),
  ];

  return (
    <CustomTable
      data={data}
      isLoading={isLoading}
      pageNumber={pageNumber}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      columns={isMultiTeam ? columns : columns.filter(col => (col as any).accessorKey !== 'team_name')}
      onPaginationChange={onPaginationChange}
      sorting={sorting}
      onSortingChange={onSortingChange}
    />
  );
};
